/**
 * The chat surfaces' one question about the UI language (#2109 R3 lane L5b).
 *
 * Server messages are English and stay English on the server (R3-B11), so
 * only an English reader is shown one; every other reader gets a localized
 * line. `en` and any `en-*` tag (the `en-XA` pseudo-locale included) count as
 * English.
 */
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, languageDisplayName } from '@/config/languages';
import { bcp47For } from '@/i18n/negotiate';

/**
 * The language the reader CHOSE (`i18n.language`), not the one whose bundle
 * has arrived (`resolvedLanguage` reads `en` until a lazy target catalog
 * loads). The copy itself falls back to English meanwhile; decisions about
 * server text, Intl output and the companion line follow the choice.
 */
export function useUiLanguage(): string {
  const { i18n } = useTranslation();
  return i18n.language ?? 'en';
}

export function isEnglishUi(language: string | undefined | null): boolean {
  const lang = language ?? 'en';
  return lang === 'en' || lang.startsWith('en-');
}

/**
 * True when the companion has no prompt path in this UI language (brief B2:
 * pt and fr are `companion: false`). The family can still type in it; the
 * reply comes back in English, and `/chat` says so (R3-B2).
 */
export function companionSpeaksUiLanguage(language: string | undefined | null): boolean {
  if (isEnglishUi(language)) return true;
  const entry = SUPPORTED_LANGUAGES.find((l) => l.code === language);
  return entry ? entry.companion : true;
}

/**
 * A language's name for the reader, never an English name in a non-English UI
 * (plan L5b, handoff translation lines). English readers keep the table's
 * English name byte-identical ("Mandarin", not Intl's "Chinese"); everyone
 * else gets `Intl.DisplayNames` in their own language, then the language's
 * own name, then the code.
 */
export function languageNameFor(code: string | undefined | null, uiLanguage: string | undefined | null): string {
  if (!code) return '';
  if (isEnglishUi(uiLanguage)) return languageDisplayName(code);
  try {
    const names = new Intl.DisplayNames([bcp47For(uiLanguage ?? 'en')], { type: 'language' });
    const name = names.of(bcp47For(code));
    if (name && name !== bcp47For(code)) return name;
  } catch {
    // An engine without DisplayNames falls through to the native name.
  }
  return SUPPORTED_LANGUAGES.find((l) => l.code === code)?.nativeName ?? code;
}
